import { assetProxyModule } from "@mem-labs/common-editor";
import { getBackendAssetProxyUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { AssetAccessTokenState } from "@/store/auth/AssetAccessTokenState";

type BuildProxiedAssetUrlArgs = {
  originalUrl: string | null | undefined;
  assetAccessTokenState: AssetAccessTokenState | null | undefined;
};

export const buildProxiedAssetUrl = ({
  originalUrl,
  assetAccessTokenState,
}: BuildProxiedAssetUrlArgs): string | null => {
  if (!originalUrl) {
    return null;
  }

  if (originalUrl.startsWith("blob:") || originalUrl.startsWith("data:")) {
    return originalUrl;
  }

  if (!assetAccessTokenState?.token) {
    return null;
  }

  const expiresAtIso = assetAccessTokenState.expiresAt?.toUTC().toISO() ?? null;

  return (
    assetProxyModule.buildProxyUrl({
      originalUrl,
      config: {
        baseUrl: getBackendAssetProxyUrl(),
        auth: {
          assetAccessToken: assetAccessTokenState.token,
          expiresAt: expiresAtIso,
        },
      },
    }) ?? null
  );
};

/**
 * Find the inline attachment node that corresponds to a search (or similar) match
 * by comparing underlying asset URLs.
 *
 * Both sides may carry common-editor's synthetic `mem-storage://proxy?url=<real>`
 * wrapper around the real GCS URL — the inline node's `downloadUrl` always does,
 * and a backend-returned `attachment_url` may too depending on how it was stored.
 * We unwrap both before comparing so the match holds regardless of wrapper state.
 *
 * This is the contractual bridge for correlating a search result to an inline
 * file/image node: `NoteAttachment.id` is derived server-side from the underlying
 * asset URL, so URL equality is the only stable identity shared across the two
 * systems (the frontend-generated node `fileId` and the backend-derived
 * `attachment_id` live in disjoint UUID namespaces).
 */
export const resolveMatchingAttachmentByUrl = <T extends { url?: string }>({
  searchAttachmentUrl,
  attachments,
}: {
  searchAttachmentUrl?: string | null;
  attachments: T[];
}): T | null => {
  if (!searchAttachmentUrl) return null;

  const unwrappedSearchUrl =
    assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: searchAttachmentUrl }) ??
    searchAttachmentUrl;

  return (
    attachments.find(attachment => {
      if (!attachment.url) return false;
      const unwrappedNodeUrl =
        assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: attachment.url }) ??
        attachment.url;
      return unwrappedNodeUrl === unwrappedSearchUrl;
    }) ?? null
  );
};
